import { useState, useEffect, useCallback } from "react";

/**
 * Tracks whether the scrollable menu list can scroll left or right,
 * and exposes scroll handlers. Uses IntersectionObserver on sentinel
 * elements placed at both ends of the list — no scroll event listeners.
 *
 * @param {React.RefObject<HTMLElement>} menuRef - ref to the scrollable <ul>
 */
const useHorizontalScroll = (menuRef) => {
  const [canScrollLeft,  setCanScrollLeft]  = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const menu = menuRef.current;
    if (!menu) return;

    const startSentinel = menu.querySelector(".scroll-sentinel-start");
    const endSentinel   = menu.querySelector(".scroll-sentinel-end");
    if (!startSentinel || !endSentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === startSentinel) setCanScrollLeft(!entry.isIntersecting);
          if (entry.target === endSentinel)   setCanScrollRight(!entry.isIntersecting);
        });
      },
      { root: menu, threshold: 0.1 },
    );

    observer.observe(startSentinel);
    observer.observe(endSentinel);
    return () => observer.disconnect();
  }, [menuRef]);

  const scrollLeft = useCallback(() => {
    const menu = menuRef.current;
    if (!menu) return;
    const isRTL = document.documentElement.dir !== "ltr";
    menu.scrollBy({ left: isRTL ? menu.clientWidth : -menu.clientWidth, behavior: "smooth" });
  }, [menuRef]);

  const scrollRight = useCallback(() => {
    const menu = menuRef.current;
    if (!menu) return;
    const isRTL = document.documentElement.dir !== "ltr";
    menu.scrollBy({ left: isRTL ? -menu.clientWidth : menu.clientWidth, behavior: "smooth" });
  }, [menuRef]);

  return { canScrollLeft, canScrollRight, scrollLeft, scrollRight };
};

export default useHorizontalScroll;
